/**
 * HTTP Client - Axios with auth interceptors and silent refresh
 * Matches chamama-booking-app error handling patterns
 */

import axios, { type AxiosInstance } from 'axios';
import * as TokenService from './token';
import { errorCoordinator } from '@/services/errorCoordinator';
import { globalNavigate } from '@/lib/navigation';

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4260/api/v1';

console.log('[HTTP] API_URL:', API_URL);
console.log('[HTTP] APP_ENV:', import.meta.env.VITE_APP_ENV);

const http: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Cache-Control': 'no-cache, no-store',
  },
});

http.interceptors.request.use(async (config) => {
  if (TokenService.checkCrossTabLogout()) {
    throw new Error('Cross-tab logout detected');
  }

  // Import dynamically to avoid circular dependency
  const { useAuthStore } = await import('@/features/auth');
  const token = useAuthStore.getState().accessToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  console.log(`[HTTP] → ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);

  return config;
});

// Single-flight refresh promise to ensure only one refresh call at a time
let refreshPromise: Promise<string> | null = null;

http.interceptors.response.use(
  (res) => {
    console.log(`[HTTP] ← ${res.status} ${res.config.method?.toUpperCase()} ${res.config.url}`);
    const sourceKey = `${res.config.method?.toUpperCase()}:${res.config.url}`;
    errorCoordinator.handleRecovery(sourceKey);
    return res;
  },
  async (err) => {
    const status = err.response?.status ?? 'NETWORK_ERROR';
    const url = err.config?.url ?? 'unknown';
    console.error(`[HTTP] ✗ ${status} ${err.config?.method?.toUpperCase()} ${url}`, err.message);
    const originalConfig = err.config;

    // Handle 401 - authentication failure
    if (err.response?.status === 401) {
      if (TokenService.checkCrossTabLogout()) {
        throw new Error('Cross-tab logout detected');
      }

      // Skip refresh for auth endpoints to avoid infinite loops
      if (
        originalConfig.url?.includes('/auth/login') ||
        originalConfig.url?.includes('/auth/register') ||
        originalConfig.url?.includes('/auth/refresh')
      ) {
        throw err;
      }

      // If impersonating, end impersonation instead of refreshing
      const { useAuthStore: authStoreForImp } = await import('@/features/auth');
      const impState = authStoreForImp.getState();
      if (impState.isImpersonating) {
        impState.stopImpersonation();
        globalNavigate('/admin/user-impersonation', { replace: true });
        return Promise.reject(new Error('Impersonation session expired'));
      }

      // Only retry once per request
      if (!originalConfig._retry) {
        originalConfig._retry = true;

        try {
          if (!refreshPromise) {
            refreshPromise = TokenService.withRefreshLock(async () => {
              try {
                // Send refresh token in body (needed for native apps where cookies don't work cross-origin)
                const storedRefreshToken = TokenService.getRefreshToken();
                const response = await http.post('/auth/refresh',
                  storedRefreshToken ? { refreshToken: storedRefreshToken } : undefined
                );
                const { accessToken, refreshToken: newRefreshToken } = response.data;

                const { useAuthStore } = await import('@/features/auth');
                useAuthStore.getState().updateTokens(accessToken);
                if (newRefreshToken) TokenService.setRefreshToken(newRefreshToken);

                return accessToken;
              } catch (refreshError: unknown) {
                const refreshErr = refreshError as { response?: { status?: number } };
                console.error('[HTTP] Token refresh failed:', refreshErr?.response?.status || 'network error');

                // Distinguish auth errors from network/server errors
                const isAuthError = refreshErr?.response?.status === 401 ||
                                    refreshErr?.response?.status === 403;

                // Clear tokens and auth state regardless
                TokenService.removeTokens();
                const { useAuthStore } = await import('@/features/auth');
                useAuthStore.getState().clearAuth();

                // Redirect to global login with appropriate reason
                if (window.location.pathname !== '/login') {
                  if (isAuthError) {
                    globalNavigate('/login?reason=session_expired', { replace: true });
                  } else {
                    globalNavigate('/login?reason=refresh_failed', { replace: true });
                  }
                }

                throw refreshError;
              } finally {
                refreshPromise = null;
              }
            });
          }

          const newAccessToken = await refreshPromise;
          originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
          return http(originalConfig);
        } catch {
          return Promise.reject(new Error('Session expired - please login again'));
        }
      }
    }

    // Handle 403/419 - forbidden or session expired
    if (err.response?.status === 403 || err.response?.status === 419) {
      if (TokenService.checkCrossTabLogout()) {
        throw new Error('Cross-tab logout detected');
      }
    }

    throw err;
  }
);

export { http };
export default http;
